import { Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { TransactionServiceOrdersService } from '../../../services/transaction-service-orders.service';
import { StatusServiceOrdersService } from '../../../services/status-service-orders.service';
import { GroupsService } from '../../../services/groups.service';
import { ConfigOrdersService } from '../../../services/config-orders.service';

export interface DirecionamentoData {
  setorDestino: string;
  grupoId: number;
  novoStatusId: number;
  novoStatusNome: string;
  transactionId: number;
  configOrderId?: number;
  destinoTypeOS?: string;
  observacao?: string;
}

export interface TransacaoDisponivel {
  transactionId: number;
  grupoId: number;
  grupoNome: string;
  novoStatusId: number;
  novoStatusNome: string;
  configOrderId?: number;
  destinoTypeOS?: string;
}

@Component({
  selector: 'app-direcionamento-modal',
  templateUrl: './direcionamento-modal.component.html',
  styleUrls: ['./direcionamento-modal.component.css']
})
export class DirecionamentoModalComponent implements OnDestroy {
  @Output() direcionamentoConfirmado = new EventEmitter<DirecionamentoData>();
  @Output() closeModal = new EventEmitter<void>();

  showModal: boolean = false;
  loading: boolean = false;
  ordemServicoAtual: string = '';

  transacoesDisponiveis: TransacaoDisponivel[] = [];
  transacaoSelecionadaId: string = '';
  observacao: string = '';
  transacaoTouched: boolean = false;

  configOrderAtualId: number | null = null;

  private groupId: number | null = null;
  private currentStatusNome: string = '';
  private destroy$ = new Subject<void>();

  constructor(
    private transactionService: TransactionServiceOrdersService,
    private statusService: StatusServiceOrdersService,
    private groupsService: GroupsService,
    private configOrdersService: ConfigOrdersService
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  open(ordemServicoAtual: string, groupId: number | null, currentStatusNome: string, configOrderId?: number | null): void {
    this.showModal = true;
    this.ordemServicoAtual = ordemServicoAtual || 'Não definida';
    this.groupId = groupId;
    this.configOrderAtualId = configOrderId ?? null;
    this.currentStatusNome = currentStatusNome || '';
    this.resetForm();
    this.carregarTransacoes();
  }

  close(): void {
    this.showModal = false;
    this.resetForm();
    this.closeModal.emit();
  }

  resetForm(): void {
    this.transacaoSelecionadaId = '';
    this.observacao = '';
    this.transacaoTouched = false;
    this.transacoesDisponiveis = [];
  }

  get configOrderMudara(): boolean {
    const selecionada = this.transacaoSelecionada;
    if (!selecionada?.configOrderId) return false;
    if (!this.configOrderAtualId) return true;
    return selecionada.configOrderId !== this.configOrderAtualId;
  }

  get textoResumoConfigOrder(): string {
    const selecionada = this.transacaoSelecionada;
    if (!selecionada) return '';

    if (!selecionada.configOrderId) {
      return 'Permanece a mesma';
    }

    return this.configOrderMudara ? 'Será alterada' : 'Permanece a mesma';
  }

  get textoDestinoTypeOS(): string {
    const selecionada = this.transacaoSelecionada;
    if (!selecionada) return '—';
    return selecionada.destinoTypeOS || 'Não definida';
  }

  get transacaoSelecionada(): TransacaoDisponivel | null {
    if (!this.transacaoSelecionadaId) return null;
    return this.transacoesDisponiveis.find(t => t.transactionId === +this.transacaoSelecionadaId) ?? null;
  }

  isFormValid(): boolean {
    return !!this.transacaoSelecionadaId;
  }

  carregarTransacoes(): void {
    if (!this.configOrderAtualId && !this.groupId) {
      this.transacoesDisponiveis = [];
      this.loading = false;
      return;
    }

    this.loading = true;

    const transactions$ = this.configOrderAtualId
      ? this.transactionService.getByConfigOrderId(this.configOrderAtualId)
      : this.transactionService.getByGroup(this.groupId!);

    const statuses$ = this.statusService.getAll();

    const groups$ = this.groupsService.getAllGroups().pipe(
      catchError(() => of([]))
    );

    const configOrders$ = this.configOrdersService.getAll().pipe(
      catchError(() => of([]))
    );

    forkJoin({
      transactions: transactions$.pipe(catchError(() => of([]))),
      statuses: statuses$.pipe(catchError(() => of([]))),
      groups: groups$,
      configOrders: configOrders$
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ transactions, statuses, groups, configOrders }) => {
        // Build status id -> name map and status id -> configOrderId map
        const statusMap = new Map<number, string>(statuses.map(s => [s.id, s.name]));
        const statusConfigMap = new Map<number, number>();
        statuses.forEach(s => { if (s.configOrderId) statusConfigMap.set(s.id, s.configOrderId); });

        const configOrderTypeMap = new Map<number, string>((configOrders as any[]).map((c: any) => [c.id, c.typeOS]));

        // Find the firstStatusId that matches the current OS status name
        // Status legado (Aberta/Aberto) sem regra: tenta aliases do workflow B2B.
        const statusAliases = this.expandStatusAliases(this.currentStatusNome);
        const filtered = transactions.filter(t => {
          const firstName = t.firstStatusName ?? (t.firstStatusId ? statusMap.get(t.firstStatusId) : undefined);
          const firstNorm = firstName?.toLowerCase().trim();
          return !!firstNorm && statusAliases.includes(firstNorm);
        });

        if (filtered.length === 0) {
          this.transacoesDisponiveis = [];
          this.loading = false;
          return;
        }

        const groupMap = new Map<number, string>(groups.map((g: any) => [g.id, g.name]));

        this.transacoesDisponiveis = filtered
          .filter(t => t.statusDirectionId != null && t.groupId != null)
          .map(t => {
            const directionName = t.statusDirectionName
              ?? (t.statusDirectionId ? statusMap.get(t.statusDirectionId) : undefined)
              ?? `Status #${t.statusDirectionId}`;

            const configOrderId = t.statusDirectionId ? statusConfigMap.get(t.statusDirectionId) : undefined;
            return {
              transactionId: t.id,
              grupoId: t.groupId!,
              grupoNome: groupMap.get(t.groupId!) ?? `Grupo #${t.groupId}`,
              novoStatusId: t.statusDirectionId!,
              novoStatusNome: directionName,
              configOrderId,
              destinoTypeOS: configOrderId ? (configOrderTypeMap.get(configOrderId) ?? undefined) : undefined
            };
          });

        this.loading = false;
      });
  }

  confirmarDirecionamento(): void {
    this.transacaoTouched = true;
    const selecionada = this.transacaoSelecionada;
    if (!selecionada) return;

    this.direcionamentoConfirmado.emit({
      setorDestino: selecionada.grupoNome,
      grupoId: selecionada.grupoId,
      novoStatusId: selecionada.novoStatusId,
      novoStatusNome: selecionada.novoStatusNome,
      transactionId: selecionada.transactionId,
      configOrderId: selecionada.configOrderId,
      destinoTypeOS: selecionada.destinoTypeOS,
      observacao: this.observacao.trim() || undefined
    });
    this.close();
  }

  /** Aberta/Aberto no legado B2B equivale às primeiras etapas do workflow. */
  private expandStatusAliases(status: string): string[] {
    const norm = (status || '').toLowerCase().trim();
    const aliases = new Set<string>();
    if (norm) aliases.add(norm);
    if (norm === 'aberta' || norm === 'aberto') {
      aliases.add('triagem');
      aliases.add('em orçamento');
      aliases.add('em orcamento');
    }
    return [...aliases];
  }
}

